import React from "react";

import _ from "lodash";
import queryString from "query-string";
import { connect } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";

import { tableActionsCreator } from "app/actions/tableActions";
import { showSuccessToast } from "app/actions/toast_actions";
import { apiHelper } from "app/common/services/apiHelper";
import categoryService from "app/common/services/categoryService";
import { ECategoryType } from "app/common/services/categoryService.types";
import { chosenSites } from "app/common/services/chosenSites";
import { swSettings } from "app/common/services/swSettings";
import { MAX_DOMAINS_IN_CATEGORY } from "app/components/customCategoriesWizard/custom-categories-max-domains";
import { TrafficShareWithVisits } from "app/components/React/Table/cells";
import { SWReactTableWrapperWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import { getToastItemComponent } from "app/components/React/Toast/ToastItem";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter, translate } from "app/filters/i18nFilter";
import { changeFilter, i18nCategoryFilter, smallNumbersPercentageFilter } from "app/filters/ngFilters";
import { StyledCoreWebsiteCell } from "app/pages/segments/analysis/StyledComponents";
import { dateToUTC } from "app/pages/website-analysis/incoming-traffic/chartConfig";
import { getOverTimeChartAbsNumsConfigSingle } from "app/pages/website-analysis/incoming-traffic/commonOverTime";
import TrafficOverTimeChartNoData from "app/pages/website-analysis/TrafficOverTimeChartNoData";
import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import CountryService from "app/services/CountryService";
import type { dateForTooltip } from "app/services/DurationService";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { PngExportService } from "app/services/PngExportService";
import { allTrackers } from "app/services/track/track";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import Chart from "pro-features/components/Chart/src/Chart";
import { ETableSelectionNewGroupDropdownMode } from "pro-features/components/TableSelection/src/TableSelectionNewGroupDropdown";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { DEFAULT_SORT_DIRECTION, DEFAULT_SORT_FIELD } from "../incomingTrafficConsts";
import { compareGenericReferralLast } from "../incomingTrafficUtils";
import { IncomingTrafficLineChart, IncomingTrafficLineChartDataTransformer } from "../IncomingTrafficLineChart";
import {
    IncomingTrafficTotalTrafficBox,
    IncomingTrafficTotalTrafficBoxTransformer,
} from "../IncomingTrafficTotalTrafficBox";
import {
    CloseIconButton,
    EnrichedRowHeaderTrafficShare,
    ReferralsPage,
    ToggleIconButton,
    TrafficOverTime,
    TrafficOverTimeChart,
    TrafficOverTimeChartTitle,
    TrafficOverTimeIndex,
    TrafficOverTimeLeft,
    TrafficOverTimeShareWithAbsWrap,
    TrafficOverTimeTitle,
    TrafficOverTimeWebSiteWrapForAbsNums,
} from "../StyledComponents";
import type { IIncomingTrafficPageData } from "../types";

import { IncomingTrafficSingleTableSettings } from "./IncomingTrafficSingleTableSettings";
import { IncomingTrafficSingleTableTopConnected } from "./IncomingTrafficSingleTableTop";

declare const similarweb;

export interface IBackendData {
    AllCategories: Array<{
        Count: number;
        Name: string;
        Value: number;
    }>;
    TopCategories: Array<{
        Count: number;
        Name: string;
        Value: number;
    }>;
    TotalShare: number;
    TotalCount: number;
    TotalUnGroupedCount: number;
    TotalVisits: number;
    TotalVisitsGlobalList: { [key: string]: number };
    Categories: { [key: string]: Array<{ Name: string; Value: number }> };
    Topics: Array<{ Name: string; Value: number }>;
}

interface IIncomingTrafficSingleProps {
    selectedRows: Array<{ Domain: string }>;
    clearAllSelectedRows: () => void;
    showToast: (href, text, label) => void;
    params: any;
}

const i18n = i18nFilter();

class IncomingTrafficSingle extends React.PureComponent<IIncomingTrafficSingleProps, any> {
    private dropdownRef;

    private swNavigator = proNavigator;

    private swSettings: typeof swSettings;

    private pageSize = 100;

    private lineChartInstance;

    constructor(props) {
        super(props);
        this.swSettings = swSettings;
        this.dropdownRef = React.createRef();
        this.state = {
            newGroupLoading: false,
            newGroupType: ECategoryType.PARTNERS_LIST,
            newGroupError: false,
            newGroupErrorMessage: null,
            allCategories: [],
            totalSectionData: null,
            tableSelectionMode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
            lineChartTitle: i18n("analysis.common.trafficsource.referrals.title"),
            lineChartTitleTooltip: i18n("analysis.common.trafficsource.referrals.title.tooltip"),
            pageNumber: 0,
            sort: this.props.params?.orderBy,
        };
        this.changePageCallback = this.changePageCallback.bind(this);
    }

    public async componentDidMount() {
        const data = await this.getPageData();
        const { lineChartData } = IncomingTrafficLineChartDataTransformer(data, chosenSites, true);
        const { totalTrafficData } = IncomingTrafficTotalTrafficBoxTransformer(data, chosenSites);
        this.setState({
            lineChartData,
            totalTrafficData,
        });
    }

    public render() {
        const downloadExcelPermitted = this.swSettings.current.resources.IsExcelAllowed;
        const { referralsCategory: category, webSource, duration } = this.props.params;
        const categoryName = category ? decodeURIComponent(category).split(",") : null;
        const durationObject = DurationService.getDurationData(duration, null, null, null);
        const { from, to, isWindow } = durationObject.raw;
        const { lineChartTitle, lineChartTitleTooltip, totalTrafficData, lineChartData } = this.state;
        const hasCustomCategoriesPermission =
            categoryService.hasCustomCategoriesPermission() && !proNavigator.isSalesSolution();
        const selectedRows = this.props.selectedRows;
        const pageSize = this.pageSize;
        const pageNumber = this.state.pageNumber;
        const durationDiff = DurationService.getDiffSymbol(from, to);
        const showTrafficOverTimeChartNoData = ["28d", "1m"].includes(durationDiff);
        const is28d = durationObject.forAPI.latest === "28d";
        return (
            <ReferralsPage className="sharedTooltip">
                <>
                    <div className="row-fluid">
                        <IncomingTrafficTotalTrafficBox
                            isLoading={!this.state.lineChartData}
                            totalTrafficData={totalTrafficData}
                            webSource={webSource}
                            to={to}
                            from={from}
                            is28d={is28d}
                        />
                        <IncomingTrafficLineChart
                            isLoading={!this.state.lineChartData}
                            isSingle={true}
                            isDaily={isWindow}
                            data={this.state.lineChartData}
                            webSource={webSource}
                            to={to}
                            from={from}
                            title={lineChartTitle}
                            titleTooltip={lineChartTitleTooltip}
                            is28d={is28d}
                            onExport={this.onLineChartExport}
                            afterRender={this.afterLineChartRender}
                        />
                    </div>
                    {lineChartData ? (
                        <SWReactTableWrapperWithSelection
                            changePageCallback={this.changePageCallback}
                            tableSelectionKey="IncomingTrafficSingle"
                            tableSelectionProperty="Domain"
                            maxSelectedRows={MAX_DOMAINS_IN_CATEGORY}
                            cleanOnUnMount={true}
                            serverApi={this.getApiEndpoint()}
                            initialFilters={this.getInitialFilters()}
                            tableColumns={this.getColumns(hasCustomCategoriesPermission, to)}
                            transformData={this.transformData}
                            getDataCallback={this.onGetData}
                            dataParamsAdapter={this.dataParamsAdapter}
                            tableOptions={{
                                metric: "MarketingWorkspaceWebsiteGroupTable",
                                tableSelectionTrackingParam: "Domain",
                                trackName: "Traffic Sources",
                                get enrichedRowComponentClass() {
                                    return `DropdownContent-container ${
                                        selectedRows && selectedRows.length > 0 ? "selected" : ""
                                    }`;
                                },
                                EnrichedRowComponent: (props) => {
                                    if (typeof props?.row === "undefined") {
                                        return null;
                                    }
                                    const { Domain, Favicon, index, TotalVisitsPerMonth, TotalShare } = props.row;
                                    const clickOutsideXButton = (e) => {
                                        allTrackers.trackEvent("Open", "Click", "Traffic Over Time/Collapsed");
                                        document.body.click();
                                    };
                                    const data = TotalVisitsPerMonth.map((item) => [dateToUTC(item.Key), item.Value]);
                                    const graphData = [{ data }];
                                    return (
                                        <div>
                                            <TrafficOverTime>
                                                <TrafficOverTimeLeft>
                                                    <div onClick={clickOutsideXButton}>
                                                        <ToggleIconButton
                                                            iconName="chev-down"
                                                            type="flat"
                                                        />
                                                    </div>
                                                    <TrafficOverTimeIndex>
                                                        {pageNumber * pageSize + index + 1}
                                                    </TrafficOverTimeIndex>
                                                    <TrafficOverTimeWebSiteWrapForAbsNums>
                                                        <StyledCoreWebsiteCell
                                                            icon={Favicon}
                                                            domain={Domain}
                                                            externalLink={`http://${Domain}`}
                                                            trackExternalLink={() =>
                                                                allTrackers.trackEvent(
                                                                    "external link",
                                                                    "click",
                                                                    `Conversion Category Overview`,
                                                                )
                                                            }
                                                        />
                                                    </TrafficOverTimeWebSiteWrapForAbsNums>
                                                    <TrafficOverTimeShareWithAbsWrap>
                                                        <TrafficOverTimeTitle>
                                                            {i18n(
                                                                "analysis.source.referrals.table.columns.share.title",
                                                            )}
                                                        </TrafficOverTimeTitle>
                                                        <EnrichedRowHeaderTrafficShare>
                                                            <TrafficShareWithVisits
                                                                {...props.row}
                                                                value={TotalShare}
                                                                row={props.row}
                                                                layout="row"
                                                                applyAbbrNumberFilter={true}
                                                            />
                                                        </EnrichedRowHeaderTrafficShare>
                                                    </TrafficOverTimeShareWithAbsWrap>
                                                    {/* hidden due to SIM-28057
                                        <TrafficOverTimeChangeWrap>
                                            <TrafficOverTimeTitle>{i18n('analysis.source.search.all.table.columns.change.title')}</TrafficOverTimeTitle>
                                            <ChangePercentage value={Change} {...props.row}/>
                                        </TrafficOverTimeChangeWrap>
*/}
                                                </TrafficOverTimeLeft>
                                                <CloseIconButton
                                                    type="flat"
                                                    onClick={clickOutsideXButton}
                                                    iconName="clear"
                                                    placement="left"
                                                />
                                            </TrafficOverTime>
                                            {!showTrafficOverTimeChartNoData ? (
                                                <TrafficOverTimeChart>
                                                    <TrafficOverTimeChartTitle>
                                                        <BoxTitle
                                                            tooltip={i18n(
                                                                "incomingtraffic.overtime.chart.title.tooltip",
                                                            )}
                                                        >
                                                            {i18n("incomingtraffic.overtime.chart.title")}
                                                        </BoxTitle>
                                                    </TrafficOverTimeChartTitle>
                                                    <Chart
                                                        type="area"
                                                        config={getOverTimeChartAbsNumsConfigSingle("line")}
                                                        data={graphData}
                                                        domProps={{
                                                            style: { height: "250px" },
                                                        }}
                                                    />
                                                </TrafficOverTimeChart>
                                            ) : (
                                                <TrafficOverTimeChartNoData />
                                            )}
                                        </div>
                                    );
                                },
                                get enrichedRowComponentHeight() {
                                    return 580;
                                },
                                shouldApplyEnrichedRowHeightToCell: false,
                                shouldEnrichRow: (props, index, e) => {
                                    const openEnrich = e?.currentTarget?.childNodes[0]?.className.includes("enrich");
                                    if (openEnrich) {
                                        allTrackers.trackEvent("Open", "Click", "Traffic Over Time/Expand");
                                    }
                                    return openEnrich;
                                },
                                onEnrichedRowClick: (isOpen, rowIndex, row) => {
                                    // console.log('onEnrichedRowClick');
                                },
                                customTableClass: "incoming-traffic-table",
                            }}
                            recordsField="records"
                            totalRecordsField="TotalCount"
                            onSort={this.onSort}
                            pageIndent={1}
                        >
                            {(topComponentProps) => (
                                <IncomingTrafficSingleTableTopConnected
                                    {...topComponentProps}
                                    selectedCategoryId={category}
                                    selectedCategory={categoryName}
                                    allCategories={this.state.allCategories}
                                    totalSectionData={this.state.totalSectionData}
                                    referralsTrafficShare={this.state.referralsTrafficShare}
                                    excelLink={this.getExcel()}
                                    downloadExcelPermitted={downloadExcelPermitted}
                                    topics={this.state.topics}
                                    categoriesData={this.state.categoriesData}
                                    domainMetaData={this.state.domainMetaData}
                                    onCheckboxChange={this.onCheckboxChange}
                                    href={this.swNavigator.href.bind(this.swNavigator)}
                                    current={this.swNavigator.current.bind(this.swNavigator)}
                                    updateParams={this.swNavigator.updateParams.bind(this.swNavigator)}
                                    params={this.props.params}
                                    selectedRows={this.props.selectedRows}
                                    clearAllSelectedRows={this.props.clearAllSelectedRows}
                                    showToast={this.props.showToast}
                                />
                            )}
                        </SWReactTableWrapperWithSelection>
                    ) : null}
                </>
            </ReferralsPage>
        );
    }

    private changePageCallback(page) {
        this.setState({ pageNumber: page });
    }

    private afterLineChartRender = (chart) => {
        this.lineChartInstance = chart;
        return {};
    };

    // We wrap this in a Promise because the DownloadMenuButton expects a promise for PNG downloads
    private onLineChartExport = (): Promise<void> => {
        return new Promise((resolve) => {
            allTrackers.trackEvent(`Download`, `submit-ok`, `Referral Visits/PNG`);
            if (this.lineChartInstance) {
                const { lineChartTitle } = this.state;
                let trimmedDateTitle =
                    lineChartTitle.indexOf("from") != -1
                        ? lineChartTitle.substr(0, lineChartTitle.indexOf("from") - 1)
                        : lineChartTitle;
                trimmedDateTitle =
                    lineChartTitle.indexOf("Last") != -1
                        ? lineChartTitle.substr(0, lineChartTitle.indexOf("Last") - 1)
                        : trimmedDateTitle;
                PngExportService.getInstance().export(this.lineChartInstance, trimmedDateTitle);
                // using setTimeout to trigger the png buttons loading state which will trigger a loading animation in the button.
                setTimeout(() => resolve(), 3000);
                return;
            }
            resolve();
        });
    };

    private dataParamsAdapter = (params) => {
        const transformedParams = { ...params };
        if (transformedParams.filter) {
            delete transformedParams.filter;
        }
        if (transformedParams.sort) {
            transformedParams.orderBy = `${transformedParams.sort} ${transformedParams.asc ? "asc" : "desc"}`;
            delete transformedParams.sort;
            delete transformedParams.asc;
        }
        if (transformedParams.referralsCategory) {
            transformedParams.Category = transformedParams.referralsCategory;
            delete transformedParams.referralsCategory;
        }
        return transformedParams;
    };

    public getColumns = (useSelection, to) => {
        const { duration, webSource, country } = this.props.params;
        const isUSState = CountryService.isUSState(country);
        const durationTooltip = _.mapValues(
            DurationService.getDurationData(duration || "", "", "").forTooltip as dateForTooltip,
            (v) => {
                return decodeURIComponent(v);
            },
        );
        const durationTooltipParams = {
            currentMonth: durationTooltip.to,
            lastMonth: durationTooltip.from,
        };

        let sortedColumn;
        if (this.state.sort) {
            const [field, sortDirection] = this.state.sort.split(" ");
            sortedColumn = {
                field,
                sortDirection,
            };
        }
        const columns = IncomingTrafficSingleTableSettings.getColumns(
            sortedColumn,
            duration === "28d",
            useSelection,
            durationTooltipParams,
            webSource,
            !to.isBefore("2018-10-01"),
            undefined,
            isUSState,
        );
        return columns;
    };

    public getInitialFilters = () => {
        const params = { ...this.props.params };
        // orderBy
        if (!params.orderBy) {
            params.orderBy = `${DEFAULT_SORT_FIELD} ${DEFAULT_SORT_DIRECTION}`;
        }
        const apiParams = apiHelper.transformParamsForAPI(params);
        return apiParams;
    };

    public getApiEndpoint = () => {
        const { webSource } = this.props.params;
        switch (webSource) {
            case "MobileWeb":
                return "/api/websiteanalysis/GetTrafficSourcesMobileWebReferralsTable";
            case "Total":
                return "/api/websiteanalysis/GetTrafficSourcesTotalReferralsTable";
            case "Desktop":
                return "/api/websiteanalysis/GetTrafficSourcesReferralsTable";
        }
    };

    public transformData = (data) => {
        const params = this.props.params;
        const keys = params.key.split(",");

        const parseTotalVisitsAndSharePerMonth = (data) => {
            const res = [];
            for (const [key, value] of Object.entries(data)) {
                res.push({ Key: key, Value: value[0].AbsValue });
            }
            return res.sort((itemA, itemB) => (itemA.Key > itemB.Key ? 1 : -1));
        };

        return {
            ...data,
            records: data.Records.map((record) => {
                return {
                    ...record,
                    TotalVisitsPerMonth: record.TotalVisitsAndSharePerMonth
                        ? parseTotalVisitsAndSharePerMonth(record.TotalVisitsAndSharePerMonth)
                        : [],
                    url: this.swNavigator.href("digitalsuite_website_websiteperformance", {
                        ...params,
                        key: record.Domain,
                    }),
                    // country: this.props.country,
                    trafficDistribution: record.SiteOrigins
                        ? Object.entries(record.SiteOrigins).map(([site, value], index) => {
                              const colorIndex = keys.indexOf(site);
                              return {
                                  color: colorsPalettes.carbon[0],
                                  backgroundColor: CHART_COLORS.main[colorIndex],
                                  width: value,
                                  // @ts-expect-error FIXME: Argument of type 'unknown' is not assignable to parameter of type 'string'
                                  text: smallNumbersPercentageFilter()(value, 1),
                                  name: site,
                              };
                          })
                        : [],
                    Rank: record.Rank === -1 ? 0 : record.Rank,
                    TotalShare: record.FilteredShare && record.FilteredShare,
                };
            }).sort(compareGenericReferralLast),
        };
    };

    public onGetData = (data: IBackendData) => {
        this.setState({
            allCategories: data.AllCategories,
            topCategories: data.TopCategories,
            totalSectionData: {
                totalShare: data.TotalShare > 0.99 ? 1 : data.TotalShare,
                totalUnGroupedCount: data.TotalCount,
                totalVisits: this.state.totalTrafficData.visits * data.TotalShare,
            },
            referralsTrafficShare: Object.entries<number>(data.TotalVisitsGlobalList).map(([domain, percentage]) => {
                return {
                    color: chosenSites.getSiteColor(domain),
                    name: domain,
                    percentage,
                    valueText: changeFilter()(percentage),
                    width: percentage * 100,
                };
            }),
            topics: data.Topics,
            categoriesData: similarweb.utils
                .formatTopList(data.TopCategories, 6, {
                    transformFunction: similarweb.utils.addCategoryIdFromName,
                })
                .map((cat) => {
                    return {
                        category: i18nCategoryFilter()(cat.Name),
                        categoryApi: cat.Id,
                        ...Object.entries(data.Categories).reduce((acc, [domain, domainCategories]) => {
                            return {
                                ...acc,
                                [domain]: cat.Value,
                            };
                        }, {}),
                    };
                }),
            domainMetaData: chosenSites.map((site) => {
                return {
                    icon: chosenSites.listInfo[site].icon,
                    name: site,
                    color: chosenSites.getSiteColor(site),
                };
            }),
        });
    };

    public getExcel = () => {
        const params = this.dataParamsAdapter(this.getInitialFilters());
        const queryStringParams = queryString.stringify(params);
        return `/export/analysis/GetTrafficSourcesReferralsTsv?${queryStringParams}`;
    };

    public showSuccessToast = (name, isNewGroup) => {
        this.props.clearAllSelectedRows();
        this.setState({
            tableSelectionMode: ETableSelectionNewGroupDropdownMode.GROUP_LIST,
            newGroupErrorMessage: null,
            newGroupError: false,
        });
        this.dropdownRef.current.close();
        const text = isNewGroup
            ? i18n("table.selection.websites.groupcreated")
            : i18n("table.selection.websites.groupupdated");
        this.showGroupLinkToast(name, text, i18n("table.selection.websites.seegroup"));
    };

    public showGroupLinkToast = (name, text, label) => {
        const params = this.props.params;
        const linkToGroup = this.swNavigator.href("digitalsuite_marketresearch_webmarketanalysis_overview", {
            ...params,
            category: `*${name}`,
        });
        this.dropdownRef.current.close();
        this.props.showToast(linkToGroup, text, label);
    };

    public onAdd = (s) => (event) => {};

    public onSort = ({ field, sortDirection }) => {
        this.swNavigator.updateParams({
            orderBy: `${field} ${sortDirection}`,
        });
        this.setState({
            sort: `${field} ${sortDirection}`,
        });
    };

    public onCheckboxChange = ({ field, value }) => {
        this.swNavigator.updateParams({ [field]: value });
    };

    public setStateAsync(newState) {
        return new Promise<void>((resolve, reject) => {
            this.setState(newState, resolve);
        });
    }

    public onListTypeSelect = (typeId) => {
        this.setState({ newGroupType: typeId });
    };

    private getPageData = (): Promise<IIncomingTrafficPageData> => {
        const params = { ...this.props.params };
        Object.keys(params).forEach((key) => params[key] === undefined && delete params[key]);
        if (params.limits) {
            delete params.limits;
        }
        const apiParams = apiHelper.transformParamsForAPI(params);
        let endpoint = "";
        switch (params.webSource) {
            case "MobileWeb":
                endpoint = "/api/websiteanalysis/GetTrafficSourcesMobileWebReferrals";
                break;
            case "Total":
                endpoint = "/api/websiteanalysis/GetTrafficSourcesTotalReferrals";
                break;
            case "Desktop":
                endpoint = "/api/websiteanalysis/GetTrafficSourcesReferrals";
                break;
        }

        return DefaultFetchService.getInstance().get(endpoint, apiParams);
    };
}

const TABLE_SELECTION_INCOMING_TRAFFIC_SINGLE_KEY = "IncomingTrafficSingle";

const mapStateToProps = (state) => {
    return {
        selectedRows: getTableSelectedItems(TABLE_SELECTION_INCOMING_TRAFFIC_SINGLE_KEY)(state),
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        clearAllSelectedRows: () => {
            dispatch(tableActionsCreator(TABLE_SELECTION_INCOMING_TRAFFIC_SINGLE_KEY, "Domain").clearAllSelectedRows()); // todo
        },
        showToast: (href, text, label) => {
            dispatch(
                showSuccessToast(
                    getToastItemComponent({
                        text,
                        linkText: label,
                        href,
                        onClick: () =>
                            allTrackers.trackEvent(
                                "add to Custom Category",
                                "click",
                                "internal link/websites.overview",
                            ),
                    }),
                ),
            );
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps, null)(IncomingTrafficSingle) as React.ComponentType<
    Partial<IIncomingTrafficSingleProps>
>;
